import type { Currency } from "@core/schema/Currency.js";
import type { DeliveryType } from "@core/schema/DeliveryType.js";
import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
import type { Locale } from "@core/schema/Locale.js";
import type { PublicOrder } from "@core/schema/Order.js";
import type { OrderPricesInput } from "@core/schema/OrderPrices.js";
import type { OrderProduct } from "@core/schema/OrderProduct.js";
import type { PaymentType } from "@core/schema/PaymentType.js";
import type { ProductAttributeGroupOfValue, ProductOfVariantForCustomer } from "@core/schema/Product.js";
import { deliveryTypeNameMap } from "@core/schema/names/deliveryTypeNameMap.js";
import { paymentTypeNameMap } from "@core/schema/names/paymentTypeNameMap.js";
import { type OrderProductPriceSettings, getPriceOfOrderProduct } from "@core/schema/utils/getPriceOfOrderProduct.js";
import { shouldApplyTax } from "@core/schema/utils/shouldApplyTax.js";
import { type OrderProductSettings, transformToOrderProduct } from "@core/schema/utils/transformToOrderProduct.js";
import { roundToPlaces } from "@core/utils/roundToPlaces.js";
import type { OrderAfterOverview } from "./cart/CartType.js";

declare const gtag: (...args: unknown[]) => void;

interface DynamicRemarketingEvent {
	event: string;
	ecommerce?: Record<string, unknown>;
	[other: string]: unknown;
}

interface Item {
	item_name: string;
	item_id: string;
	item_variant?: string;
	price: number;
	quantity: number;
}

function getProductItem(orderProduct: OrderProduct, locale: Locale, settings: OrderProductPriceSettings): Item {
	const price = getPriceOfOrderProduct({ ...orderProduct, quantity: 1 }, settings);

	return {
		item_name: orderProduct.info.name[locale],
		item_id: orderProduct.info.id,
		item_variant: orderProduct.info.variant.label[locale],
		price: roundToPlaces(price.withoutTax, 2),
		quantity: orderProduct.quantity,
	};
}

export function reportProductDetail(
	locale: Locale,
	product: ProductOfVariantForCustomer,
	variantAttributes: ProductAttributeGroupOfValue[],
	settings: OrderProductSettings,
): void {
	const orderProduct = transformToOrderProduct(product, variantAttributes, settings, 1);
	const applyTax = shouldApplyTax(settings.destinationCountry, EconomicSubjectVatType.NotVatSubject);
	const item = getProductItem(orderProduct, locale, { ...settings, applyTax });
	const items = [item];

	const event: DynamicRemarketingEvent = {
		event: "view_item",
		ecommerce: {
			currency: settings.currency,
			value: roundToPlaces(
				items.reduce((prev, curr) => curr.price + prev, 0),
				2,
			),
			items,
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportAddedProductToCart(
	product: OrderProduct,
	locale: Locale,
	currency: Currency,
	settings: OrderProductPriceSettings,
): void {
	const price = getPriceOfOrderProduct(product, settings);
	const event: DynamicRemarketingEvent = {
		event: "add_to_cart",
		ecommerce: {
			currency,
			value: roundToPlaces(price.withoutTax, 2),
			items: [getProductItem(product, locale, settings)],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportRemovedProductFromCart(
	product: OrderProduct,
	locale: Locale,
	currency: Currency,
	settings: OrderProductPriceSettings,
	itemQuantity?: number,
): void {
	const quantity = itemQuantity ?? product.quantity;
	const productOfQuantity = { ...product, quantity };
	const price = getPriceOfOrderProduct(productOfQuantity, settings);
	const event: DynamicRemarketingEvent = {
		event: "remove_from_cart",
		ecommerce: {
			currency,
			value: roundToPlaces(price.withoutTax, 2),
			items: [getProductItem(productOfQuantity, locale, settings)],
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportCheckoutBegan(
	order: OrderAfterOverview,
	price: OrderPricesInput,
	locale: Locale,
	settings: OrderProductPriceSettings,
): void {
	const event: DynamicRemarketingEvent = {
		event: "begin_checkout",
		ecommerce: {
			currency: order.payment?.currency,
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			items: Object.values(order.products).map((item) => getProductItem(item, locale, settings)),
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function onPaymentTypeChanged(
	order: OrderAfterOverview,
	price: OrderPricesInput,
	locale: Locale,
	paymentType: PaymentType,
	settings: OrderProductPriceSettings,
): void {
	const event: DynamicRemarketingEvent = {
		event: "add_payment_info",
		ecommerce: {
			currency: order.payment?.currency,
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			payment_type: paymentTypeNameMap[paymentType][locale],
			items: Object.values(order.products).map((item) => getProductItem(item, locale, settings)),
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function onDeliveryTypeChanged(
	order: OrderAfterOverview,
	price: OrderPricesInput,
	locale: Locale,
	deliveryType: DeliveryType,
	settings: OrderProductPriceSettings,
): void {
	const event: DynamicRemarketingEvent = {
		event: "add_shipping_info",
		ecommerce: {
			currency: order.payment?.currency,
			value: roundToPlaces(price.totalPrice.withoutTax, 2),
			shipping_tier: deliveryTypeNameMap[deliveryType][locale],
			items: Object.values(order.products).map((item) => getProductItem(item, locale, settings)),
		},
	};

	gtag("event", event.event, event.ecommerce);
}

export function reportPurchase(order: PublicOrder): void {
	const priceWithTax = order.prices.totalPrice.withTax;
	const priceWithoutTax = order.prices.totalPrice.withoutTax;
	const event: DynamicRemarketingEvent = {
		event: "purchase",
		ecommerce: {
			currency: order.payment.currency,
			value: roundToPlaces(priceWithoutTax, 2),
			tax: roundToPlaces(priceWithTax - priceWithoutTax, 2),
			shipping: roundToPlaces(order.prices.deliveryPrice.withTax, 2),
			transaction_id: order.goPayData?.id ?? "",
			items: order.products.map((item) =>
				getProductItem(item, order.locale, { applyTax: priceWithTax !== priceWithoutTax }),
			),
		},
	};

	gtag("event", event.event, event.ecommerce);
}
